import {
  useState, useEffect, useCallback, useRef,
} from 'react';

const WORKER_URL = process.env.REACT_APP_WORKER_URL || 'wss://suh-pelada-scoreboard.pandlr.workers.dev';

// Conexão do controle remoto / relógio com o worker. Recebe o snapshot do app e envia comandos.
export function useRemoteScore() {
  const [remoteState, setRemoteState] = useState(null); // snapshot do app (players/teams/config)
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState(null);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  // true quando o usuário desconectou (ou a tela saiu) — aí não reconecta sozinho.
  const manualCloseRef = useRef(false);

  const connect = useCallback(() => {
    manualCloseRef.current = false;
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    const { current } = wsRef;
    if (current && (current.readyState === WebSocket.OPEN
      || current.readyState === WebSocket.CONNECTING)) return;

    try {
      const ws = new WebSocket(WORKER_URL);

      ws.onopen = () => {
        setIsConnected(true);
        setError(null);
      };

      ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          if (message.type === 'state') {
            setRemoteState(message.data);
          }
        } catch (err) {
          console.error('Error parsing message:', err);
        }
      };

      ws.onclose = () => {
        if (wsRef.current !== ws) return; // conexão antiga, já substituída
        wsRef.current = null;
        if (manualCloseRef.current) return;
        setIsConnected(false);
        reconnectTimeoutRef.current = setTimeout(connect, 3000);
      };

      ws.onerror = () => {
        if (wsRef.current !== ws || manualCloseRef.current) return;
        setError('Erro de conexão');
        setIsConnected(false);
      };

      wsRef.current = ws;
    } catch (err) {
      setError(err.message);
    }
  }, []);

  const disconnect = useCallback(() => {
    manualCloseRef.current = true;
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    setIsConnected(false);
  }, []);

  // Envia um comando para o app aplicar (ex.: marcar ponto, criar jogador, check-in, montar time).
  const sendCommand = useCallback((command) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'command', data: command }));
    }
  }, []);

  useEffect(() => () => {
    disconnect();
  }, [disconnect]);

  return {
    remoteState,
    isConnected,
    error,
    connect,
    disconnect,
    sendCommand,
  };
}
